import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  selectCurrentMap,
  selectUserMaps,
  selectSharedMaps,
  selectViewMode,
  setCurrentMap,
  setViewMode,
  selectMapsLoading,
  selectCurrentMapId,
} from "../../store/mapsSlice";

interface MapTabsNavigationProps {
  onCreateNewMap?: () => void;
}

const MapTabsNavigation: React.FC<MapTabsNavigationProps> = ({
  onCreateNewMap,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { language } = useSelector((state: RootState) => state.ui);
  const currentMap = useSelector(selectCurrentMap);
  const currentMapId = useSelector(selectCurrentMapId);
  const userMaps = useSelector(selectUserMaps);
  const sharedMaps = useSelector(selectSharedMaps);
  const viewMode = useSelector(selectViewMode);
  const loading = useSelector(selectMapsLoading);

  const allMaps = [...userMaps, ...sharedMaps];

  const handleMapSelect = (mapId: string) => {
    if (mapId !== currentMapId) {
      dispatch(setCurrentMap(mapId));
    }
  };

  const handleViewModeToggle = () => {
    dispatch(setViewMode(viewMode === "tabs" ? "grid" : "tabs"));
  };

  const truncateText = (text: string, maxLength: number = 20) => {
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  if (loading) {
    return (
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
            <span className="text-sm text-gray-500">
              {language === "he" ? "טוען מפות..." : "Loading maps..."}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Grid view mode - show view toggle button only
  if (viewMode === "grid") {
    return (
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {language === "he" ? "בחירת מפה" : "Map Selection"}
          </h3>
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <button
              onClick={handleViewModeToggle}
              className="btn-secondary flex items-center text-sm"
              title={
                language === "he" ? "חזור לתצוגת טאבים" : "Back to tabs view"
              }
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h7"
                />
              </svg>
              {language === "he" ? "טאבים" : "Tabs"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tabs view mode
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="px-4 py-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <h3 className="text-sm font-medium text-gray-700">
              {language === "he" ? "מפות" : "Maps"} ({allMaps.length})
            </h3>
            {/* {currentMap && (
                            <div className="text-xs text-gray-500">
                                <span className="mr-2">
                                    {currentMap.accessLevel === 'owner' ? (
                                        language === 'he' ? 'בעלים' : 'Owner'
                                    ) : (
                                        language === 'he' ? `משותף (${currentMap.accessLevel})` : `Shared (${currentMap.accessLevel})`
                                    )}
                                </span>
                                {currentMap.lastAccessedAt && (
                                    <span>
                                        {language === 'he' ? 'גישה אחרונה:' : 'Last accessed:'} {' '}
                                        {new Date(currentMap.lastAccessedAt).toLocaleDateString(
                                            language === 'he' ? 'he-IL' : 'en-US'
                                        )}
                                    </span>
                                )}
                            </div>
                        )} */}
          </div>

          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            {allMaps.length > 6 && (
              <button
                onClick={handleViewModeToggle}
                className="btn-secondary flex items-center text-sm"
                title={language === "he" ? "תצוגת רשת" : "Grid view"}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                  />
                </svg>
                {language === "he" ? "רשת" : "Grid"}
              </button>
            )}
            {onCreateNewMap && (
              <button
                onClick={onCreateNewMap}
                className="btn-primary flex items-center text-sm"
                title={language === "he" ? "צור מפה חדשה" : "Create new map"}
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                {language === "he" ? "מפה חדשה" : "New Map"}
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        {allMaps.length > 0 ? (
          <div className="flex items-center space-x-1 rtl:space-x-reverse overflow-x-auto">
            {allMaps.slice(0, 10).map((map) => (
              <button
                key={map._id}
                onClick={() => handleMapSelect(map._id)}
                className={`
                                    flex-shrink-0 px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors duration-200 min-w-0
                                    ${
                                      currentMapId === map._id
                                        ? "text-violet-600 border-purple-600 bg-blue-50"
                                        : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                                    }
                                `}
                title={`${map.name}${
                  map.description ? ` - ${map.description}` : ""
                }`}
              >
                <div className="flex items-center space-x-2 rtl:space-x-reverse min-w-0">
                  {/* Map type icon */}
                  <div className="flex-shrink-0">
                    {map.accessLevel === "owner" ? (
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V8zm0 4a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-3 h-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                      </svg>
                    )}
                  </div>

                  {/* Map name */}
                  <span className="truncate min-w-0">
                    {truncateText(map.name)}
                  </span>

                  {/* Access count badge */}
                  {map.accessCount > 0 && (
                    <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {map.accessCount}
                    </span>
                  )}
                </div>
              </button>
            ))}

            {/* Show more indicator if there are more than 10 maps */}
            {allMaps.length > 10 && (
              <button
                onClick={handleViewModeToggle}
                className="flex-shrink-0 px-2 py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors duration-200"
                title={
                  language === "he"
                    ? `+${allMaps.length - 10} מפות נוספות`
                    : `+${allMaps.length - 10} more maps`
                }
              >
                +{allMaps.length - 10}
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="text-gray-400 mb-2">
              <svg
                className="w-8 h-8 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                />
              </svg>
            </div>
            <p className="text-sm text-gray-500">
              {language === "he" ? "אין מפות זמינות" : "No maps available"}
            </p>
            {onCreateNewMap && (
              <button
                onClick={onCreateNewMap}
                className="mt-2 btn-primary text-sm"
              >
                {language === "he" ? "צור מפה ראשונה" : "Create your first map"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapTabsNavigation;
